/**
 * <predictive-search-component>
 *
 * Header search with a predictive dropdown, following the Horizon theme approach:
 * - the dropdown markup is rendered by Liquid (sections/predictive-search.liquid)
 *   and fetched through the Section Rendering API on /search/suggest
 * - elements are wired with `ref` attributes instead of class selectors
 * - requests are debounced, cached per query and aborted when a newer one starts
 * - the input is an ARIA combobox, results are options navigable with the keyboard
 *
 * The same dropdown is used on desktop and mobile; layout is handled in predictive-search.css.
 */

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 1;
const VIEWPORT_GUTTER = 16;

/** Results are shared between every search bar on the page (desktop header, mobile header, mobile menu). */
const resultsCache = new Map();

let instanceCount = 0;

class PredictiveSearchComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number | undefined} */
  #debounceTimer;
  #activeIndex = -1;
  #uid = `PredictiveSearch-${++instanceCount}`;

  /**
   * Elements marked with `ref="name"` inside this component.
   * @returns {{ form: HTMLFormElement, input: HTMLInputElement, resetButton: HTMLButtonElement,
   *   dropdown: HTMLElement, results: HTMLElement, status: HTMLElement }}
   */
  get refs() {
    const refs = {};
    for (const element of this.querySelectorAll('[ref]')) {
      if (element.closest('predictive-search-component') === this) {
        refs[element.getAttribute('ref')] = element;
      }
    }
    return refs;
  }

  get isOpen() {
    return !this.refs.dropdown.hidden;
  }

  get searchTerm() {
    return this.refs.input.value.trim();
  }

  /** @returns {HTMLElement[]} */
  get options() {
    return Array.from(this.refs.results.querySelectorAll('[role="option"]'));
  }

  connectedCallback() {
    const { input, dropdown, form, resetButton } = this.refs;

    dropdown.id = `${this.#uid}-listbox`;
    input.setAttribute('aria-controls', dropdown.id);
    this.#toggleResetButton();

    input.addEventListener('input', this.#onInput);
    input.addEventListener('focus', this.#onFocus);
    input.addEventListener('keydown', this.#onKeydown);
    form.addEventListener('submit', this.#onSubmit);
    resetButton.addEventListener('click', this.#onReset);
    this.addEventListener('focusout', this.#onFocusOut);
    this.refs.results.addEventListener('pointermove', this.#onPointerMove);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  disconnectedCallback() {
    const { input, form, resetButton } = this.refs;

    input?.removeEventListener('input', this.#onInput);
    input?.removeEventListener('focus', this.#onFocus);
    input?.removeEventListener('keydown', this.#onKeydown);
    form?.removeEventListener('submit', this.#onSubmit);
    resetButton?.removeEventListener('click', this.#onReset);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.refs.results?.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    window.removeEventListener('resize', this.#updateMaxHeight);
    window.visualViewport?.removeEventListener('resize', this.#updateMaxHeight);

    this.#abortController?.abort();
    clearTimeout(this.#debounceTimer);
  }

  /* Event handlers */

  #onInput = () => {
    this.#toggleResetButton();
    clearTimeout(this.#debounceTimer);

    if (this.searchTerm.length < MIN_QUERY_LENGTH) {
      this.#abortController?.abort();
      this.#clearResults();
      this.close();
      return;
    }

    this.#debounceTimer = setTimeout(() => this.#search(this.searchTerm), DEBOUNCE_MS);
  };

  #onFocus = () => {
    const term = this.searchTerm;
    if (term.length < MIN_QUERY_LENGTH) return;

    if (resultsCache.has(this.#cacheKey(term))) {
      this.#render(term, resultsCache.get(this.#cacheKey(term)));
    } else {
      this.#search(term);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        if (!this.isOpen) return;
        event.preventDefault();
        this.#moveSelection(1);
        break;
      case 'ArrowUp':
        if (!this.isOpen) return;
        event.preventDefault();
        this.#moveSelection(-1);
        break;
      case 'Enter': {
        const activeOption = this.options[this.#activeIndex];
        if (!this.isOpen || !activeOption) return;
        event.preventDefault();
        activeOption.click();
        break;
      }
      case 'Escape':
        if (!this.isOpen) return;
        event.preventDefault();
        this.close();
        break;
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (!this.searchTerm) {
      event.preventDefault();
      this.refs.input.focus();
      return;
    }
    this.close();
  };

  /** @param {MouseEvent} event */
  #onReset = (event) => {
    event.preventDefault();
    const { input } = this.refs;
    input.value = '';
    this.#abortController?.abort();
    clearTimeout(this.#debounceTimer);
    this.#clearResults();
    this.close();
    this.#toggleResetButton();
    input.focus();
  };

  /** Close when keyboard focus leaves the component (mouse/touch is handled on pointerdown). */
  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && !this.contains(next)) this.close();
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (this.isOpen && !this.contains(/** @type {Node} */ (event.target))) this.close();
  };

  /** Keep mouse hover and keyboard selection in sync. */
  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    const option = /** @type {HTMLElement} */ (event.target).closest?.('[role="option"]');
    if (!option) return;
    const index = this.options.indexOf(option);
    if (index !== this.#activeIndex) this.#setActiveOption(index, { scroll: false });
  };

  /* Search */

  /** @param {string} term */
  #cacheKey(term) {
    return term.toLowerCase();
  }

  /** @param {string} term */
  async #search(term) {
    const cacheKey = this.#cacheKey(term);

    if (resultsCache.has(cacheKey)) {
      this.#render(term, resultsCache.get(cacheKey));
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.toggleAttribute('loading', true);

    try {
      const response = await fetch(this.#buildUrl(term), { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Predictive search failed with status ${response.status}`);

      const html = this.#extractSectionHtml(await response.text());
      resultsCache.set(cacheKey, html);

      // Ignore responses for a term the shopper has already typed past.
      if (this.#cacheKey(this.searchTerm) !== cacheKey) return;
      this.#render(term, html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.close();
    } finally {
      this.toggleAttribute('loading', false);
    }
  }

  /** @param {string} term */
  #buildUrl(term) {
    const { suggestUrl, sectionId, productsLimit } = this.dataset;
    const params = new URLSearchParams({
      q: term,
      section_id: sectionId || 'predictive-search',
      'resources[type]': 'product,collection,query,page,article',
      'resources[limit]': productsLimit || '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag,author',
    });
    return `${suggestUrl || '/search/suggest'}?${params.toString()}`;
  }

  /** @param {string} responseText */
  #extractSectionHtml(responseText) {
    const doc = new DOMParser().parseFromString(responseText, 'text/html');
    const section = doc.querySelector('.shopify-section') ?? doc.body;
    return section.innerHTML;
  }

  /**
   * @param {string} term
   * @param {string} html
   */
  #render(term, html) {
    const { results, status } = this.refs;
    results.innerHTML = html;

    this.options.forEach((option, index) => {
      option.id = `${this.#uid}-option-${index}`;
    });
    this.#setActiveOption(-1);

    status.textContent = results.querySelector('[data-predictive-search-live-text]')?.textContent.trim() ?? '';

    if (term) this.open();
  }

  #clearResults() {
    this.refs.results.innerHTML = '';
    this.refs.status.textContent = '';
    this.#setActiveOption(-1);
  }

  /* Open / close */

  open() {
    if (this.isOpen) return;
    this.refs.dropdown.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');
    this.setAttribute('open', '');
    this.#updateMaxHeight();
    window.addEventListener('resize', this.#updateMaxHeight);
    window.visualViewport?.addEventListener('resize', this.#updateMaxHeight);
  }

  close() {
    if (!this.isOpen) return;
    this.refs.dropdown.hidden = true;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
    this.#setActiveOption(-1);
    window.removeEventListener('resize', this.#updateMaxHeight);
    window.visualViewport?.removeEventListener('resize', this.#updateMaxHeight);
  }

  /** Limit the dropdown to the visible viewport, which shrinks when the mobile keyboard is open. */
  #updateMaxHeight = () => {
    const { dropdown } = this.refs;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const top = dropdown.getBoundingClientRect().top;
    const maxHeight = Math.max(200, viewportHeight - top - VIEWPORT_GUTTER);
    this.style.setProperty('--predictive-search-max-height', `${Math.round(maxHeight)}px`);
  };

  /* Keyboard selection */

  /** @param {number} step */
  #moveSelection(step) {
    const count = this.options.length;
    if (!count) return;

    let next = this.#activeIndex + step;
    if (next >= count) next = -1;
    if (next < -1) next = count - 1;
    this.#setActiveOption(next);
  }

  /**
   * @param {number} index -1 returns the selection to the input
   * @param {{ scroll?: boolean }} [options]
   */
  #setActiveOption(index, { scroll = true } = {}) {
    const options = this.options;
    this.#activeIndex = index;

    options.forEach((option, optionIndex) => {
      option.setAttribute('aria-selected', String(optionIndex === index));
    });

    const activeOption = options[index];
    if (activeOption) {
      this.refs.input.setAttribute('aria-activedescendant', activeOption.id);
      if (scroll) activeOption.scrollIntoView({ block: 'nearest' });
    } else {
      this.refs.input.removeAttribute('aria-activedescendant');
    }
  }

  #toggleResetButton() {
    this.refs.resetButton.hidden = this.refs.input.value.length === 0;
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
